import { FunctionComponent, ReactNode, useEffect, useState } from "react";
import TextFieldAtom from "../../../../atoms/textfield";
import IconButton from "../../../buttons/icon";
import {
  LabelPairedMinusSmBoldIcon,
  LabelPairedPlusSmBoldIcon,
} from "@deriv/quill-icons";

export type TextFieldStepperType = {
  id?: string;
  name?: string;
  className?: string;
  label?: string;
  labelRight?: string;
  message?: string;
  placeholder?: string;

  /** Variant props */
  indicator?: "neutral" | "fail" | "success";
  state?: "default" | "disabled";
  type?: "outline" | "fill";
  textAlignment?: "left" | "center";
  size?: "lg" | "md" | "sm";

  /** Custom props */
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  iconStatus?: ReactNode;
  labelLeft?: string;
  isRequired?: boolean;
  optionalText?: string;
  maxLength?: number;
  messages?: {
    text: string;
    type: "neutral" | "fail" | "success";
    icon?: ReactNode;
  }[];
  onChange?: (value: string | number) => void;
  variant?: "labelled" | "labelless";
  value?: string | number;
  decimalPlaces?: number;
  maxValue?: number;
  minValue?: number;
  increment?: string | number;
  split?: boolean;
};

export const TextFieldStepper: FunctionComponent<TextFieldStepperType> = ({
  maxValue = 1000,
  minValue = 0,
  decimalPlaces = 0,
  value: initialValue,
  variant = "labelled",
  state,
  onChange,
  increment = 1,
  split = false,
  ...props
}) => {
  const [value, setValue] = useState(initialValue ?? 0);

  const handleChange = (value: string | number) => {
    const formattedValue = formatFloats(value);
    setValue(formattedValue);
    onChange?.(formattedValue);
  };

  const formatFloats = (value: string | number): number => {
    // Handle empty/invalid input
    if (value === null || value === "" || isNaN(Number(value))) {
      return 0;
    }

    let numValue = Number(value);

    // If number is too large or in scientific notation, cap it at maxValue
    if (!Number.isFinite(numValue) || numValue > maxValue) {
      return maxValue;
    }

    // If number is too small, cap it at minValue
    if (numValue < minValue) {
      return minValue;
    }

    // Format to specified decimal places
    return Number(numValue.toFixed(decimalPlaces));
  };

  const validateInput = (nextChar: string | number) => {
    const disallowedKeys = ["e", "E"];

    // Check if the key is allowed
    if (disallowedKeys.includes(nextChar.toString())) return false;

    return true;
  };

  useEffect(() => {
    if (value !== undefined) {
      const formattedValue = parseFloat(value.toString()).toFixed(
        decimalPlaces
      );
      setValue(formattedValue);
    }
  }, [value, maxValue, decimalPlaces]);

  const minusButton = (
    <IconButton
      style="tertiary"
      size="sm"
      state={state === "disabled" ? "disabled" : "default"}
      icon={<LabelPairedMinusSmBoldIcon />}
      onClick={() => handleChange(Number(value || 0) - Number(increment))}
      color="black"
    />
  );

  const plusButton = (
    <IconButton
      style="tertiary"
      size="sm"
      state={state === "disabled" ? "disabled" : "default"}
      icon={<LabelPairedPlusSmBoldIcon />}
      onClick={() => handleChange(Number(value || 0) + Number(increment))}
      color="black"
    />
  );

  let iconExtra,
    iconLeft,
    iconRight,
    textAlignment: "left" | "center" = "left";
  iconRight = plusButton;

  if (split) {
    iconLeft = minusButton;
    textAlignment = "center";
  } else {
    iconExtra = minusButton;
  }

  return (
    <TextFieldAtom
      {...props}
      inputType="number"
      onChange={handleChange}
      value={value}
      state={state}
      variant={variant}
      iconExtra={iconExtra}
      iconLeft={iconLeft}
      iconRight={iconRight}
      textAlignment={textAlignment}
      onBeforeChange={(nextChar: string | number) => validateInput(nextChar)}
    />
  );
};

TextFieldStepper.displayName = "TextFieldStepper";

export default TextFieldStepper;
